<template>
  <Layout
    :description="props.meta.description"
    :title="props.meta.title"
    :image="props.meta.image"
  >
    <div class="overflow-y-auto overscroll-auto max-h-[100dvh]">
      <div
        class="min-h-[100dvh] pt-4 md:p-6 flex justify-center items-end md:items-center"
      >
        <div
          class="flex flex-col-reverse md:flex-col max-w-[600px] w-full gap-y-4"
        >
          <div
            class="bg-white shadow-lg rounded-t-xl md:rounded-xl overflow-hidden"
          >
            <div
              class="bg-tertiary p-5 border-b border-utility-gray-200 flex justify-between"
            >
              <div
                class="text-primary flex items-center gap-x-2 text-sm font-medium"
              >
                <template v-if="task.status == 'scheduled'">
                  <Icon name="calendar-default" class="text-utility-blue-500" />
                  {{ t("admin.insights.tasks.scheduled.title") }}
                </template>
                <template v-if="task.status == 'completed'">
                  <Icon name="check-circle" class="text-utility-success-500" />
                  {{ t("admin.insights.tasks.completed.title") }}
                </template>
                <template v-if="task.status == 'open'">
                  <Icon name="placeholder" class="text-utility-gray-500" />
                  {{ t("admin.insights.tasks.open.title") }}
                </template>
              </div>
              <div class="text-primary text-xs">
                {{ t("components.share_task.logged") }}
                {{ format(new Date(task.updated_at), "EE d/L/Y hh:mma") }}
              </div>
            </div>
            <div
              class="pt-6 pb-10 px-6 md:px-8 overflow-y-auto overflow-x-hidden h-full"
            >
              <div class="flex flex-col gap-y-2">
                <h1
                  class="text-primary text-2xl font-semibold"
                  v-if="task.description"
                >
                  {{ task.description }}
                </h1>
                <div class="text-utility-gray-700">
                  {{ task.remarks }}
                </div>
                <div v-if="task.photos.length">
                  <div
                    class="-mx-8 pb-1 -mb-1 px-8 w-[calc(100%+4rem)] flex gap-4 overflow-x-auto"
                  >
                    <div
                      v-for="(image, index) in task.photos"
                      :key="index"
                      class="p-2 shadow rounded-md"
                    >
                      <Image
                        class="w-[120px]"
                        :url="image.thumbnail_url || image.url"
                        @click="handleViewImage(image.id)"
                      />
                    </div>
                  </div>
                  <ImageSlideshow
                    :open="openImageViewer"
                    :initialIndex="activeImageIndex"
                    :images="task.photos"
                    @close="openImageViewer = false"
                  />
                </div>
              </div>
              <Divider class="mt-6 mb-6" />
              <div
                class="text-sm flex flex-col-reverse md:flex-col gap-y-6 w-full"
              >
                <div>
                  <Divider class="block md:hidden mt-0 mb-6" />

                  <Row
                    class="mb-10 md:mb-0"
                    :title="t('components.share_task.site_info')"
                  >
                    <div class="flex flex-col gap-y-1">
                      <div
                        class="flex items-center gap-2 overflow-hidden -mt-0.5"
                      >
                        <SiteImage :src="task.tenant.logo_url" />
                        <span class="font-bold text-sm text-primary truncate">{{
                          task.tenant.name
                        }}</span>
                      </div>
                      <div v-if="task.tenant.address">
                        {{ task.tenant.address }}
                      </div>
                      <div v-if="task.tenant.postal_code || task.tenant.city">
                        {{ task.tenant.postal_code }} {{ task.tenant.city }}
                      </div>
                      <div v-if="task.tenant.country">
                        {{ task.tenant.country }}
                      </div>
                    </div>
                  </Row>
                </div>

                <div class="flex flex-col gap-y-6 w-full flex-wrap">
                  <Row
                    :title="t('components.share_task.linked_to')"
                    :placeholder="t('components.share_task.no_linked_to')"
                  >
                    <div
                      v-if="breadcrumbs.length"
                      class="flex items-center gap-x-1 flex-wrap"
                    >
                      <div
                        :key="breadcrumb.id"
                        v-for="breadcrumb in breadcrumbs"
                        class="inline-flex items-center gap-1 text-sm text-primary font-medium"
                      >
                        <span>
                          {{ breadcrumb.title }}
                        </span>
                        <Icon v-if="!breadcrumb.last" name="chevron-right" />
                      </div>
                    </div>
                  </Row>

                  <Row :title="t('components.share_task.priority')">
                    <PriorityBadge
                      :priority="task.priority.id"
                      :label="task.priority.name"
                    />
                  </Row>

                  <Row
                    :title="t('components.share_task.category')"
                    :placeholder="t('components.share_task.no_category')"
                  >
                    <Badge v-if="task.category" variant="category">
                      <span class="truncate">
                        {{ task.category.name }}
                      </span>
                    </Badge>
                  </Row>

                  <Row
                    :title="t('components.share_task.due_date')"
                    :placeholder="t('components.share_task.no_date')"
                  >
                    <div
                      v-if="task.schedule_for"
                      class="text-primary text-sm font-medium"
                    >
                      {{ format(new Date(props.task.schedule_for), "dd/L/Y") }}
                    </div>
                  </Row>

                  <Row
                    :title="t('components.share_task.assignees')"
                    :placeholder="t('components.share_task.no_assignees')"
                  >
                    <div
                      v-if="task.assignees.length"
                      class="flex flex-col gap-y-3"
                    >
                      <div
                        v-for="assignee in task.assignees"
                        :key="assignee.id"
                        class="flex gap-x-2 items-center text-sm font-medium text-primary"
                      >
                        <UserAvatar :user="assignee" size="sm" />
                        {{ assignee.fullname }}
                      </div>
                    </div>
                  </Row>
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-end px-4 md:px-0">
            <div class="w-[70px]">
              <Logo />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script setup>
import Row from "@/Pages/Share/Row.vue";
import { Badge } from "@/Shadcn/Components/ui/badge";
import { Divider } from "@/Components/Details";
import { useI18n } from "vue-i18n";
import { PriorityBadge } from "@/Components/Badges";
import { computed, ref } from "vue";
import { format } from "date-fns";
import { Icon } from "@/SharedComponents/Icon";
import Logo from "@/Pages/Share/Logo.vue";
import { SiteImage } from "@/Components/Site";
import { UserAvatar } from "@/Components/Users";
import Layout from "@/Layouts/Layout.vue";
import { ImageSlideshow } from "@/Components/Images";
import { Image } from "@/Components/Images";

const { t } = useI18n();

const breadcrumbs = computed(() => {
  return props.task.spaces.map((item, index) => ({
    id: item.id,
    title: item.name,
    last: index == props.task.spaces.length - 1,
  }));
});

const props = defineProps({
  task: Object,
  meta: Object,
});

const activeImageIndex = ref(undefined);
const openImageViewer = ref(undefined);

function handleViewImage(id) {
  activeImageIndex.value = props.task.photos.findIndex(
    (image) => image.id === id
  );
  openImageViewer.value = true;
}
</script>
